import Color from 'color';
import type {Options, SeriesOptionsType} from 'highcharts';
import CubeIndex from '../cube/CubeIndex';
import {itemColor} from '../render/colors';
import {axisLabelFormat, categoryLabel, countHtml, escapeHtml, formatValue} from '../render/format';
import {isLowBase, lowBaseAttributes} from '../render/lowBase';
import {MarkRenderer, RenderContext, StatsKey} from '../types';
import {positions, resolveChannels, seriesColorMeasure, xPointColor} from './xySeries';

// Highcharts fills boxes white by default, which glares in the dark theme and ignores the series colour
const FILL_OPACITY = 0.25;

function boxFill(color: string): string {
    return Color(color).alpha(FILL_OPACITY).rgb().string();
}

function statsLabel(key: StatsKey, ctx: RenderContext): string {
    const label = escapeHtml(ctx.labels?.[key] ?? key);
    return label.charAt(0).toUpperCase() + label.slice(1);
}

const renderBoxplot: MarkRenderer = (cube, spec, ctx) => {
    const index = new CubeIndex(cube);
    const channels = resolveChannels(spec.encoding, cube.measures.length);
    const xItems = index.items(channels.x);
    const seriesItems = channels.series ? index.items(channels.series) : [undefined];
    const colorByX = seriesItems.length === 1;

    const series: SeriesOptionsType[] = seriesItems.map((seriesItem, seriesIndex) => {
        const seriesMeasure = seriesItem?.measure ?? cube.measures[0];
        const color = itemColor(seriesItem?.member, seriesColorMeasure(channels, seriesMeasure), seriesIndex, ctx);

        // Points keep their x so categories stay aligned when a member has no stats.
        const data = xItems.flatMap((xItem, xIndex) => {
            const measure = xItem.measure ?? seriesMeasure;
            const cell = index.value(positions(channels, xItem, seriesItem), measure.id);
            if (!cell?.stats) {
                return [];
            }
            const [low, q1, median, q3, high] = cell.stats;
            const lowBase = isLowBase(cube, cell);
            const pointColor = colorByX ? xPointColor(xItem, xIndex, color, ctx) : color;
            const heading = [seriesItem && seriesItem.label !== xItem.label ? seriesItem.label : '', xItem.label]
                .filter(Boolean)
                .map(escapeHtml)
                .join('<br>');
            const line = (key: StatsKey, value: number) => `${statsLabel(key, ctx)}: ${formatValue(value, measure, ctx)}`;

            return [{
                x: xIndex,
                low,
                q1,
                median,
                q3,
                high,
                ...(pointColor !== color ? {color: pointColor, fillColor: boxFill(pointColor), medianColor: pointColor} : {}),
                ...(lowBase ? lowBaseAttributes('bar', pointColor, ctx) : {}),
                custom: {
                    count: cell.count,
                    lowBase,
                    // Top-down like the box itself
                    tooltip: `<span style="color:${pointColor}">●</span> ${heading}<br>`
                        + `${line('max', high)}<br>`
                        + `${line('q3', q3)}<br>`
                        + `<b>${line('median', median)}</b><br>`
                        + `${line('q1', q1)}<br>`
                        + `${line('min', low)}<br>`
                        + countHtml(cell, lowBase),
                },
            }];
        });

        return {
            type: 'boxplot',
            id: seriesItem?.id ?? seriesMeasure.id,
            name: escapeHtml(seriesItem?.label ?? seriesMeasure.label),
            color,
            fillColor: boxFill(color),
            medianColor: color,
            data,
        } as SeriesOptionsType;
    });

    const measure = cube.measures[0];
    const options: Options = {
        chart: {type: 'boxplot'},
        title: {text: undefined},
        credits: {enabled: false},
        xAxis: {categories: xItems.map(categoryLabel)},
        yAxis: {
            title: {text: undefined},
            labels: {format: axisLabelFormat(measure)},
            ...(measure?.scale ? {min: measure.scale.min, max: measure.scale.max} : {}),
        },
        legend: {enabled: seriesItems.length > 1},
        tooltip: {shared: false, headerFormat: '', pointFormat: '{point.custom.tooltip}'},
        series,
    };

    return {type: 'highcharts', options};
};

export default renderBoxplot;
